import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './OpenTicketModal.module.css';

const EMPTY_FORM = { subject: '', category: 'general', priority: 'medium', source: '', message: '' };
const MIN_QUERY_LENGTH = 2;
const MIN_MESSAGE_LENGTH = 20;
const DEBOUNCE_MS = 300;
const LISTBOX_ID = 'open-ticket-client-listbox';

const CATEGORY_OPTIONS = [
  ['general', 'General'], ['billing', 'Billing'], ['order', 'Order Issue'],
  ['account', 'Account'], ['prescription', 'Prescription'], ['shipping', 'Shipping'],
  ['medication', 'Medication'], ['technical', 'Technical'],
  ['agreement_question', 'Agreement Question'], ['plan_question', 'Plan Question'],
  ['password_help', 'Password Help'], ['other', 'Other'],
];
const PRIORITY_OPTIONS = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']];
const SOURCE_OPTIONS = [
  ['', 'Select source...'], ['member_portal', 'Member Portal'],
  ['email', 'Email (support@ovalcare.com)'], ['phone_call', 'Phone Call'],
  ['internal_follow_up', 'Internal Follow-Up'], ['support_inbox', 'Support Inbox'], ['other', 'Other'],
];

const SelectField = ({ id, label, value, options, onChange }) => (
  <div className={styles.formGroup}>
    <label className={styles.formLabel} htmlFor={id}>{label}</label>
    <select id={id} className={styles.formSelect} value={value} onChange={onChange}>
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>{optionLabel}</option>
      ))}
    </select>
  </div>
);

const formatClientName = (client) =>
  `${client?.firstName ?? ''} ${client?.lastName ?? ''}`.trim() || 'No Name';

// Two customers can share a display name, so an option carries the two fields that
// stay unique. Measured 2026-09-19 over 88 rows: 69 carry a member ID - 44 a real one
// (`686771581`, `TESTDEV10004467`) and 25 a legacy value equal to the email. The equal
// case collapses so an option never prints one string twice, and a missing ID is fine.
const formatClientDetail = (client) => {
  const id = client?.memberId && client.memberId !== client?.email ? `ID ${client.memberId}` : '';
  return [client?.email, id].filter(Boolean).join(' · ');
};

// Mirrors the listbox's three transient states into a live region. Ordered exactly
// as the listbox renders them: loading wins, then the error, then the zero-match
// message. Returns '' while closed so nothing is announced before a search runs.
const deriveSearchStatus = ({ isOpen, isLoading, error, resultCount }) => {
  if (!isOpen) return '';
  if (isLoading) return 'Searching...';
  if (error) return error;
  return resultCount === 0 ? 'No customers match that search.' : '';
};

const ClientField = ({ selected, onSelect, disabled }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const runSearch = useCallback((value) => {
    clearTimeout(debounceRef.current);
    setError('');
    if (value.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setIsOpen(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/admin/members/search?q=${encodeURIComponent(value.trim())}`);
        setResults(response.results ?? []);
      } catch (err) {
        setResults([]);
        setError(err?.message || 'Client search failed. Try again.');
      } finally {
        setIsLoading(false);
        setActiveIndex(-1);
      }
    }, DEBOUNCE_MS);
  }, []);

  const handleChange = (e) => {
    const { value } = e.target;
    setQuery(value);
    if (selected) onSelect(null);
    runSearch(value);
  };

  const handleSelect = (client) => {
    onSelect(client);
    setQuery(formatClientName(client));
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    // Modal listens for Escape on `document`; React runs this handler at the root
    // container first, so stopping propagation here closes the LIST and leaves the
    // modal open. With the list already closed, Escape falls through and closes the
    // modal, which is the ARIA combobox contract.
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      return;
    }
    if (!isOpen || results.length === 0) return;
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
    e.preventDefault();
    if (e.key === 'Enter') handleSelect(results[activeIndex >= 0 ? activeIndex : 0]);
    else if (e.key === 'ArrowDown') setActiveIndex((i) => (i + 1) % results.length);
    else setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
  };

  return (
    <div className={styles.formGroup} ref={rootRef}>
      <label className={styles.formLabel} htmlFor="open-ticket-client">Client *</label>
      <div className={styles.comboboxWrap}>
        <input
          id="open-ticket-client"
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${LISTBOX_ID}-${activeIndex}` : undefined}
          className={styles.formInput}
          data-testid="open-ticket-client-input"
          placeholder="Search by name, email or member ID..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          disabled={disabled}
          autoComplete="off"
          required
        />
        {isOpen && (
          <ul className={styles.listbox} id={LISTBOX_ID} role="listbox" aria-label="Client search results">
            {isLoading && (
              <li className={styles.listboxState} data-testid="open-ticket-client-loading">
                <LoadingSpinner size="small" /><span>Searching...</span>
              </li>
            )}
            {!isLoading && error && (
              <li className={styles.listboxError} data-testid="open-ticket-client-error">{error}</li>
            )}
            {!isLoading && !error && results.length === 0 && (
              <li className={styles.listboxState} data-testid="open-ticket-client-empty">No customers match that search.</li>
            )}
            {!isLoading && !error && results.map((result, index) => (
              <li
                key={result.id}
                id={`${LISTBOX_ID}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.option} ${index === activeIndex ? styles.optionActive : ''}`}
                data-testid="open-ticket-client-option"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(result)}
              >
                <span className={styles.optionName}>{formatClientName(result)}</span>
                <span className={styles.optionDetail}>{formatClientDetail(result)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* The three transient search states are ANNOUNCED here, not merely drawn:
          a non-`option` <li> inside role="listbox" is not reliably read out.
          Rendered unconditionally so the region exists before its text changes. */}
      <p
        className="sr-only"
        role="status"
        aria-live="polite"
        data-testid="open-ticket-client-status"
      >
        {deriveSearchStatus({ isOpen, isLoading, error, resultCount: results.length })}
      </p>
      {selected && (
        <p className={styles.selectedClient} data-testid="open-ticket-client-selected">
          Ticket will be created for <strong>{formatClientName(selected)}</strong>
          {formatClientDetail(selected) ? ` — ${formatClientDetail(selected)}` : ''}
        </p>
      )}
    </div>
  );
};

// One create-ticket modal for both admin entry points. Pass `memberId` from a
// member profile (no Client field); omit it on the global Support Tickets page and
// the required Client picker becomes the first control.
const OpenTicketModal = ({ isOpen, onClose, memberId = null, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [client, setClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const requiresClient = !memberId;
  const targetMemberId = memberId ?? client?.id ?? null;

  // Derived every render rather than seeded from a lazy initialiser — LEARNINGS:10185
  // records a capability that silently died when a mount-only initialiser stopped
  // re-running after this page gained tabs.
  const canSubmit = Boolean(
    targetMemberId
    && form.subject.trim()
    && form.message.trim().length >= MIN_MESSAGE_LENGTH
    && !submitting,
  );

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setClient(null);
    onClose();
  };

  const updateField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requiresClient && !client) { toast.error('Select a client first'); return; }
    if (!form.subject.trim()) { toast.error('Subject is required'); return; }
    if (form.message.trim().length < MIN_MESSAGE_LENGTH) {
      toast.error(`Message must be at least ${MIN_MESSAGE_LENGTH} characters`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/admin/members/${targetMemberId}/tickets`, {
        subject: form.subject.trim(),
        category: form.category,
        priority: form.priority,
        ...(form.source && { source: form.source }),
        message: form.message.trim(),
      });
      const ticketNumber = response.ticket?.ticketNumber ?? response.ticket?.ticket_number ?? '';
      toast.success(`Ticket #${ticketNumber} opened successfully`);
      setForm(EMPTY_FORM);
      setClient(null);
      onClose();
      onCreated?.(response.ticket);
    } catch (error) {
      toast.error(error?.message || 'Failed to open ticket');
    }
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose} title="Open Support Ticket" size="medium" manageFocus>
      <div className={styles.modalContent}>
        <p className={styles.modalNote}>
          This ticket will be created on behalf of the member. They will receive an email notification.
        </p>
        <form onSubmit={handleSubmit} data-testid="open-ticket-form">
          {requiresClient && (
            <ClientField selected={client} onSelect={setClient} disabled={submitting} />
          )}
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="open-ticket-subject">Subject *</label>
            <input id="open-ticket-subject" type="text" className={styles.formInput} value={form.subject}
              onChange={updateField('subject')} placeholder="Brief description of the issue" maxLength={100} required />
          </div>
          <div className={styles.formRow}>
            <SelectField id="open-ticket-category" label="Category" value={form.category}
              options={CATEGORY_OPTIONS} onChange={updateField('category')} />
            <SelectField id="open-ticket-priority" label="Priority" value={form.priority}
              options={PRIORITY_OPTIONS} onChange={updateField('priority')} />
          </div>
          <SelectField id="open-ticket-source" label="Source / Contact Method" value={form.source}
            options={SOURCE_OPTIONS} onChange={updateField('source')} />
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="open-ticket-message">Message *</label>
            <textarea id="open-ticket-message" className={`${styles.formInput} ${styles.textarea}`} value={form.message}
              onChange={updateField('message')} rows={4} required
              placeholder={`Detailed description (minimum ${MIN_MESSAGE_LENGTH} characters)`} />
          </div>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={resetAndClose} disabled={submitting} type="button">Cancel</Button>
            <Button type="submit" disabled={!canSubmit} data-testid="open-ticket-submit">
              {submitting ? 'Creating...' : 'Create Ticket'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default OpenTicketModal;
